import legacyLandings from "./data/legacy-landings.json";
import seoUpdates from "./data/seo-updates.json";
import { defaultSettings, seedProjects } from "./domain/seed";

export const SITE_URL = "https://yuriwinchester.com.br";
export const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/assets/yuri-hero.webp`;

type SeoKind = "home" | "page" | "profile" | "project" | "service";

interface LandingSeoSource {
  slug: string;
  title: string;
  description: string;
  h1: string;
  serviceType: string;
  faq: Array<{ q: string; a: string }>;
}

export interface SeoRoute {
  path: string;
  title: string;
  description: string;
  kind: SeoKind;
  index: boolean;
  sitemap: boolean;
  heading: string;
  serviceType?: string;
  faq?: Array<{ q: string; a: string }>;
  lastModified?: string;
}

const coreRoutes: SeoRoute[] = [
  {
    path: "/",
    title: "Yuri Winchester | Engenheiro de Software em Brasília",
    description: "Engenharia de software, sites, sistemas, integrações, automação e IA em Brasília, com atendimento remoto para todo o Brasil.",
    kind: "home",
    index: true,
    sitemap: true,
    heading: "Yuri Winchester",
  },
  {
    path: "/sobre",
    title: "Sobre Yuri Winchester | Engenheiro de Software",
    description: "Conheça a trajetória, a experiência e os princípios de engenharia de software de Yuri Winchester, desenvolvedor full stack em Brasília.",
    kind: "profile",
    index: true,
    sitemap: true,
    heading: "Tecnologia com contexto, critério e consequência.",
  },
  {
    path: "/experiencia",
    title: "Experiência Profissional | Yuri Winchester",
    description: "Experiência desde 2008 em sistemas web, APIs, integrações, plataformas SaaS, automações e aplicações para operações reais.",
    kind: "page",
    index: true,
    sitemap: true,
    heading: "A complexidade cresceu. O compromisso permaneceu.",
  },
  {
    path: "/projetos",
    title: "Projetos de Software e Automações | Yuri Winchester",
    description: "Cases de software, integrações e automações com contexto, decisões técnicas, contribuição, evidências e trade-offs.",
    kind: "page",
    index: true,
    sitemap: true,
    heading: "Decisões técnicas em contexto real.",
  },
  {
    path: "/metodo",
    title: "Método de Engenharia e IA | Yuri Winchester",
    description: "Como Yuri Winchester combina contexto, alternativas, inteligência artificial, testes e revisão humana para entregar software confiável.",
    kind: "page",
    index: true,
    sitemap: true,
    heading: "Explorar muito. Decidir com evidência.",
  },
  {
    path: "/contato",
    title: "Contato | Yuri Winchester em Brasília, DF",
    description: "Converse com Yuri Winchester sobre criação de sites, sistemas, integrações, automações e produtos digitais em Brasília ou remotamente.",
    kind: "page",
    index: true,
    sitemap: true,
    heading: "Vamos conversar sobre seu site ou sistema?",
  },
];

const projectRoutes: SeoRoute[] = seedProjects
  .filter((project) => project.status === "published" && !project.deletedAt)
  .map((project) => ({
    path: `/projetos/${project.slug}`,
    title: `${project.seoTitle} | Yuri Winchester`,
    description: project.seoDescription,
    kind: "project",
    index: true,
    sitemap: true,
    heading: project.title,
  }));

// A trava era uma lista de aprovadas com um slug só, o que deixou doze páginas
// de serviço servindo 200 e pedindo noindex ao Google. O conteúdo delas foi
// medido: 252 a 306 palavras, texto próprio por nicho, seções e FAQ — patamar
// da página que já estava indexada. A lista agora é de pendências, e está
// vazia: publicar é o padrão, bloquear é que precisa de justificativa.
const pendingLandingSlugs = new Set<string>();

const landingRoutes: SeoRoute[] = (legacyLandings as LandingSeoSource[]).map((landing) => {
  const reviewed = !pendingLandingSlugs.has(landing.slug);
  return {
    path: `/${landing.slug}`,
    title: landing.title,
    description: landing.description,
    kind: "service",
    index: reviewed,
    sitemap: reviewed,
    heading: landing.h1,
    serviceType: landing.serviceType,
    faq: landing.faq,
  };
});

// Datas editoriais registradas por página; alterações no build não renovam conteúdo.
const editorialUpdates: Partial<Record<string, string>> = seoUpdates;
export const seoRoutes: SeoRoute[] = [...coreRoutes, ...projectRoutes, ...landingRoutes].map((route) => ({
  ...route,
  lastModified: editorialUpdates[route.path],
}));
export const prerenderRoutes = seoRoutes.map((route) => route.path);

export function normalizePath(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

export function getSeoRoute(pathname: string) {
  return seoRoutes.find((route) => route.path === normalizePath(pathname));
}

const personId = `${SITE_URL}/#yuri-winchester`;
const websiteId = `${SITE_URL}/#website`;
const businessId = `${SITE_URL}/#negocio`;

// Área de atuação declarada em um só lugar: o Service da landing e o
// ProfessionalService do negócio precisam responder a mesma coisa para o
// Google, senão os sinais locais se contradizem.
const AREA_SERVED = [
  { "@type": "City", name: "Brasília" },
  { "@type": "AdministrativeArea", name: "Distrito Federal" },
  { "@type": "Country", name: "Brasil" },
];

function personSchema() {
  return {
    "@type": "Person",
    "@id": personId,
    name: defaultSettings.professionalName,
    url: `${SITE_URL}/sobre`,
    image: DEFAULT_SOCIAL_IMAGE,
    jobTitle: "Engenheiro de Software e Desenvolvedor Full Stack",
    email: defaultSettings.email,
    telephone: defaultSettings.phoneE164,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Brasília",
      addressRegion: "DF",
      addressCountry: "BR",
    },
    knowsAbout: [
      "Engenharia de software",
      "Desenvolvimento web",
      "Criação de sites",
      "Integração de APIs",
      "Automação de processos",
      "Inteligência artificial aplicada ao desenvolvimento",
    ],
  };
}

// Negócio prestador de serviço, não loja física: sem vitrine, o endereço para
// o Google é a área atendida somada às coordenadas da cidade. CEP e logradouro
// ficam de fora de propósito — dado que não existe não entra em schema.
function professionalServiceSchema() {
  return {
    "@type": "ProfessionalService",
    "@id": businessId,
    name: `${defaultSettings.professionalName} — Criação de Sites e Sistemas em Brasília`,
    url: SITE_URL,
    image: DEFAULT_SOCIAL_IMAGE,
    email: defaultSettings.email,
    telephone: defaultSettings.phoneE164,
    founder: { "@id": personId },
    employee: { "@id": personId },
    inLanguage: "pt-BR",
    currenciesAccepted: "BRL",
    // O piso corresponde apenas a site institucional ou landing page.
    foundingDate: "2008",
    priceRange: "A partir de R$ 400",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Criação de sites e sistemas",
      itemListElement: [
        {
          "@type": "Offer",
          name: "Site institucional ou landing page",
          priceCurrency: "BRL",
          price: "400",
          priceSpecification: {
            "@type": "PriceSpecification",
            priceCurrency: "BRL",
            minPrice: "400",
            valueAddedTaxIncluded: true,
          },
          availability: "https://schema.org/InStock",
        },
      ],
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Brasília",
      addressRegion: "DF",
      addressCountry: "BR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: -15.7939,
      longitude: -47.8828,
    },
    areaServed: AREA_SERVED,
    serviceType: [
      "Criação de sites em Brasília",
      "Sites para empresas em Brasília",
      "Desenvolvimento web Brasília DF",
      "Desenvolvimento de sistemas sob medida",
      "Integração de APIs e automação",
    ],
    knowsLanguage: "pt-BR",
  };
}

export function jsonLdForRoute(route: SeoRoute) {
  const canonical = `${SITE_URL}${route.path === "/" ? "" : route.path}`;
  const graph: Array<Record<string, unknown>> = [
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: SITE_URL,
      name: "Atlas de Sistemas | Yuri Winchester",
      inLanguage: "pt-BR",
      publisher: { "@id": personId },
    },
    personSchema(),
    professionalServiceSchema(),
    {
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: route.title,
      description: route.description,
      inLanguage: "pt-BR",
      isPartOf: { "@id": websiteId },
      about: { "@id": personId },
      ...(route.lastModified ? { dateModified: route.lastModified } : {}),
    },
  ];

  if (route.kind === "profile") {
    graph.push({
      "@type": "ProfilePage",
      "@id": `${canonical}#profile`,
      mainEntity: { "@id": personId },
      ...(route.lastModified ? { dateModified: route.lastModified } : {}),
    });
  }

  if (route.kind === "service") {
    graph.push({
      "@type": "Service",
      "@id": `${canonical}#service`,
      name: route.serviceType,
      description: route.description,
      url: canonical,
      provider: { "@id": businessId },
      areaServed: AREA_SERVED,
    });
    if (route.faq?.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: route.faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      });
    }
  }

  if (route.kind === "project") {
    graph.push({
      "@type": "CreativeWork",
      "@id": `${canonical}#project`,
      name: route.heading,
      description: route.description,
      url: canonical,
      creator: { "@id": personId },
      inLanguage: "pt-BR",
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export function serializeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}
